import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { PageHeaderComponent } from '../shared/components/page-header/page-header.component';
import { LoadingSpinnerComponent } from '../shared/components/loading-spinner/loading-spinner.component';
import { RouterModule, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { Subscription, interval } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { environment } from '../../environments/environment';

interface TripRow {
  trip_id: number;
  name: string | null;
  start_time: string | null;
  end_time: string | null;
  active: boolean;
  report_status: string | null;  // null | "pending" | "generating" | "complete" | "failed"
  duration_minutes: number | null;
  distance_miles: number | null;
  total_catches: number | null;
}

@Component({
  selector: 'app-trips',
  templateUrl: './trips.component.html',
  styleUrls: ['./trips.component.scss'],
  standalone: true,
  imports: [
    CommonModule, RouterModule, DatePipe,
    MatTableModule, MatProgressSpinnerModule, MatButtonModule, MatChipsModule, PageHeaderComponent, LoadingSpinnerComponent
  ],
})
export class TripsComponent implements OnInit, OnDestroy {
  dataSource = new MatTableDataSource<TripRow>();
  displayedColumns = ['name', 'start_time', 'duration', 'total_catches', 'distance'];
  isLoading = true;

  // Track active polls by trip_id
  private polls: Map<number, Subscription> = new Map();

  constructor(private http: HttpClient, private router: Router) {}

  ngOnInit(): void {
    this.loadTrips();
  }

  loadTrips(): void {
    this.isLoading = true;
    this.http.get<TripRow[]>(`${environment.apiUrl}/trip_reports`).subscribe({
      next: (rows) => {
        this.dataSource.data = rows;
        this.isLoading = false;
        // Resume polling for any in-progress reports
        rows
          .filter(r => r.report_status === 'pending' || r.report_status === 'generating')
          .forEach(r => this.startPolling(r.trip_id));
      },
      error: () => { this.isLoading = false; }
    });
  }

  generate(tripId: number, event: Event): void {
    event.stopPropagation();
    // Cancel any existing poll so we can restart it after re-publishing
    this.polls.get(tripId)?.unsubscribe();
    this.polls.delete(tripId);
    // Optimistically update the row
    this.updateRow(tripId, { report_status: 'pending' });

    this.http.post<{ status: string }>(`${environment.apiUrl}/trip_report/${tripId}/generate`, {})
      .subscribe({
        next: () => this.startPolling(tripId),
        error: () => this.updateRow(tripId, { report_status: 'failed' }),
      });
  }

  private startPolling(tripId: number): void {
    if (this.polls.has(tripId)) return;

    const sub = interval(3000).pipe(
      switchMap(() => this.http.get<{ status: string }>(`${environment.apiUrl}/trip_report/${tripId}`)),
      takeWhile(r => r.status === 'pending' || r.status === 'generating', true)
    ).subscribe({
      next: (report: any) => {
        this.updateRow(tripId, {
          report_status: report.status,
          duration_minutes: report.duration_minutes,
          distance_miles: report.distance_miles,
          total_catches: report.total_catches,
        });
        if (report.status !== 'pending' && report.status !== 'generating') {
          this.polls.delete(tripId);
        }
      },
      error: () => this.polls.delete(tripId),
    });

    this.polls.set(tripId, sub);
  }

  private updateRow(tripId: number, patch: Partial<TripRow>): void {
    const rows = this.dataSource.data.map(r =>
      r.trip_id === tripId ? { ...r, ...patch } : r
    );
    this.dataSource.data = rows;
  }

  formatDuration(minutes: number | null): string {
    if (minutes === null) return '—';
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }

  viewReport(tripId: number, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/trip', tripId]);
  }

  ngOnDestroy(): void {
    this.polls.forEach(sub => sub.unsubscribe());
  }
}
